<template>
  <div class="volume-picker-page">
    <div class="container-fluid mt-5 mb-5 px-4">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h2>New Transaction</h2>
        <div class="d-flex align-items-center gap-3">
          <NewSaleLinksComponent current="forward" />
          <!-- Creating a deal is the whole page's action, not one section's, so it sits with Cancel
               at the top right as the sketch draws it (FIX4-1 item 5a). -->
          <router-link
            to="/admin/transactions"
            class="btn btn-outline-secondary"
          >
            {{ $t("forwards.picker.cancel") }}
          </router-link>
          <ActionButtonWithSpinner
            :busy="creating"
            :disabled="!canConfirm"
            variant="btn-primary"
            size="md"
            data-testid="picker-confirm"
            @click="startConfirm"
          >
            {{
              creating
                ? $t("forwards.picker.confirming")
                : $t("forwards.picker.confirm")
            }}
          </ActionButtonWithSpinner>
        </div>
      </div>

      <p
        v-if="confirmBlockedReason"
        class="text-body-tertiary fs-10 text-end mb-3"
        data-testid="picker-confirm-blocked"
      >
        {{ confirmBlockedReason }}
      </p>

      <div v-if="createError" class="error-note mb-3" role="alert">
        <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
        <span>{{ createError }}</span>
      </div>

      <PickerDealCardComponent v-model="identity" :showNameError />

      <!-- The card header carries what the card is and what it binds; inside it there is nothing but
         the three control rows, so the body opens on the first control (FIX4-2 points 3 and 4). -->
      <div class="card mb-3" data-testid="picker-filters-card">
        <div class="card-header bg-light">
          <h5 class="mb-0">{{ $t("forwards.picker.filters.title") }}</h5>
          <p class="text-body-tertiary fs-10 mb-0 mt-1">
            {{ $t("forwards.picker.criteria.description") }}
          </p>
        </div>
        <div class="card-body">
          <PickerProductionRangeComponent v-model="period" />
          <PickerCriteriaFiltersComponent
            v-model="criteria"
            v-model:minPrice="minPriceFilter"
            :productionUntil="period.until"
          />
        </div>
      </div>

      <h3 class="mb-3">{{ $t("forwards.picker.mandates.title") }}</h3>

      <div class="row g-3">
        <div class="col-12 col-lg picker-take-column">
          <PickerTakeTableComponent
            v-model="takes"
            :rows="rows"
            :ineligible="mandates?.ineligible ?? []"
            :productWarnings="productWarnings"
            :hint="hint"
            :loading="loading"
            :error="loadError"
            @retry="loadMandates"
            @update:rowsInScope="rowsInScope = $event"
          />
        </div>
        <div class="col-12 col-lg-auto">
          <PickerSummaryColumnComponent
            :rows="rows"
            :rowsInScope="rowsInScope"
            :takes="takes"
            :qualificationDate="mandates?.qualification_date ?? null"
          />
        </div>
      </div>

      <div class="card mt-3">
        <div class="card-body py-2 text-body-tertiary fs-10">
          {{ $t("forwards.picker.footer_note") }}
        </div>
      </div>
    </div>

    <!-- A mandate edited from the take table's mandate links can change what it lists and releases. -->
    <MandateEditorModalHost @written="loadMandates" />
  </div>
</template>

<script setup lang="ts">
import ActionButtonWithSpinner from "@/components/common/ActionButtonWithSpinner.vue";
import { useAccountStore } from "@/composables/account/useAccountStore";
import { dealRoute } from "@/forwards2/deals/dealRoute";
import { forwardsApi, forwardsErrorMessage } from "@/forwards2/forwardsApi";
import { isForwardsStaff } from "@/forwards2/forwardsGuard";
import type {
  MinimumPriceHint,
  PickerConfirmRequest,
  PickerMandateRow,
  PickerMandates,
  PickerQuery,
} from "@/forwards2/forwardsTypes";
import type { TakeMap } from "@/forwards2/picker/distributeTakes";
import NewSaleLinksComponent from "@/forwards2/picker/NewSaleLinksComponent.vue";
import PickerCriteriaFiltersComponent from "@/forwards2/picker/PickerCriteriaFiltersComponent.vue";
import PickerDealCardComponent from "@/forwards2/picker/PickerDealCardComponent.vue";
import PickerProductionRangeComponent from "@/forwards2/picker/PickerProductionRangeComponent.vue";
import PickerSummaryColumnComponent from "@/forwards2/picker/PickerSummaryColumnComponent.vue";
import PickerTakeTableComponent from "@/forwards2/picker/PickerTakeTableComponent.vue";
import MandateEditorModalHost from "@/forwards2/mandates/MandateEditorModalHost.vue";
import {
  confirmRequest,
  type DealIdentityForm,
  defaultPeriodForm,
  type EligibilityCriteriaForm,
  emptyCriteriaForm,
  emptyDealIdentityForm,
  periodRange,
  pickerQuery,
  type PickerPeriodForm,
} from "@/forwards2/picker/pickerForm";
import { debounce, pickBy } from "lodash-es";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

/**
 * The volume picker: a forward deal from the product definition to the takes that back it.
 *
 * One page, read top to bottom in the layout of the spot sale page it sits beside (FIX4-1 item 5,
 * sketch 1d): the deal's identity where the spot flow has its buyer card, the range and criteria
 * where it has its filter panel, and the take table beside its summary column where it has the
 * portfolio-item table beside its breakdowns. It shares no state with the transaction form (plan
 * Appendix D row 1d): a forward deal is not a spot sale with different inventory, and its buyer,
 * prices and delivery plan are set on the deal page afterwards. Its route admits staff only (the
 * `/admin` guard). A sales admin opens it read-only (user decision, 2026-09-28): every read
 * is its to make, but the confirm is `SUPER`, so the button is disabled with the reason and the
 * confirm path never sends it.
 *
 * The take table follows the form the way the spot table follows its filters — debounced, from the
 * first render onward. Its *rows* are a function of the query alone — the criteria and the range,
 * never the deal's name — so only a changed query re-asks for them; the set of taken mandates re-asks
 * for the selection's advice instead, the price hint and the product warnings, which describe which
 * mandates are selected rather than how much each gives. Taking volume
 * therefore never replaces the rows under the trader's caret (FIX4-4 point 4), and volume alone
 * changes nothing at all.
 *
 * Each mandate's take is capped at what its sales strategy releases today, and the answer leaves out a
 * mandate whose strategy has released none of its volume yet, with the date more is released. There is
 * no override here: volume past a strategy is backed slice by slice on the deal page, where the back
 * and resize dialogs offer it to a super admin.
 */

const { t } = useI18n();
const router = useRouter();
const { globalErrorHandler } = useGlobalHandlers();

const REFETCH_DEBOUNCE_MS = 400;

let mandateRequestGeneration = 0;
let adviceRequestGeneration = 0;
let acceptedMandatesGeneration = 0;

const identity = ref<DealIdentityForm>(emptyDealIdentityForm());
const criteria = ref<EligibilityCriteriaForm>(emptyCriteriaForm());
/** Narrows the rows on screen; never part of the created deal. */
const minPriceFilter = ref<number | null>(null);
const period = ref<PickerPeriodForm>(defaultPeriodForm());
const takes = ref<TakeMap>({});

const mandates = ref<PickerMandates | null>(null);
const acceptedPickerQuery = ref<PickerQuery | null>(null);
const hint = ref<MinimumPriceHint | null>(null);
const productWarnings = ref<string[]>([]);
const loading = ref(false);
const loadError = ref<unknown>(null);
const creating = ref(false);
const createError = ref<string | null>(null);

/** Set when the mandate query changes: the next refresh re-queries the rows instead of the advice. */
const rowsOutdated = ref(true);

// The route guard loaded the account before this page opened.
const accountStore = useAccountStore();
const canCreateDeals = computed(() => isForwardsStaff(accountStore.account));

const periods = computed(() => periodRange(period.value));
const rows = computed(() => mandates.value?.rows ?? []);

/**
 * The rows the take table's header filters leave on screen. The table owns those filters, so the
 * summary column beside it can only describe the same set by being told what it is.
 */
const rowsInScope = ref<PickerMandateRow[]>([]);

const takenMandateIds = computed(() =>
  Object.entries(takes.value)
    .filter(([, volume]) => volume > 0)
    .map(([mandateId]) => Number(mandateId))
    .sort((left, right) => left - right)
);

/** Stable identity for "which mandates are selected" — the only take change worth a refetch. */
const takenMandateKey = computed(() => takenMandateIds.value.join(","));

/** Stable identity for "which mandates can serve this", i.e. the request minus the selection. */
const mandateQueryKey = computed(() =>
  JSON.stringify(
    pickerQuery(criteria.value, minPriceFilter.value, periods.value, [])
  )
);

/**
 * A take above what its mandate's strategy has released. The input already marks it invalid and the
 * backend would refuse the deal, so the confirm waits for it to be lowered.
 */
const takeOverCapacity = computed(() =>
  rows.value.some(
    (row) => (takes.value[row.mandate_id] ?? 0) > row.capacity_total
  )
);

/**
 * Why the deal cannot be created yet, or null when it can — the sentence beside the disabled button.
 *
 * A confirm needs an answer on screen. Without one — the load failed, so the rows are gone and the
 * takes were never pruned — confirming would create commitments from mandates nothing is rendering,
 * against the form's range rather than the answer's (RV2-deal-creation-01). Nor may it act on the
 * answer to an earlier form: from a criteria or range change until the new answer lands, the rows
 * on screen and the confirm request still describe the previous product.
 */
const confirmBlockedReason = computed(() => {
  if (!canCreateDeals.value)
    return t("forwards.common.super_admin_only_create");
  if (identity.value.name.trim().length === 0)
    return t("forwards.picker.criteria.name_required");
  if (periods.value.length === 0)
    return t("forwards.picker.period.invalid_range");
  if (rowsOutdated.value || loading.value)
    return t("forwards.picker.take.refresh_pending");
  if (mandates.value === null) return t("forwards.picker.take.load_failed");
  if (takenMandateIds.value.length === 0)
    return t("forwards.picker.errors.no_takes");
  if (takeOverCapacity.value) return t("forwards.picker.errors.over_capacity");
  return null;
});

const canConfirm = computed(() => confirmBlockedReason.value === null);

/** The name is the one field nothing else on the page explains, so it is marked once it is all that
 *  stands between the trader and a deal. */
const showNameError = computed(
  () =>
    identity.value.name.trim().length === 0 && takenMandateIds.value.length > 0
);

/** Drops takes on mandates this answer no longer offers, so nothing invisible reaches the confirm (RV-picker-02). */
const pruneTakes = (loaded: PickerMandates) => {
  const offered = new Set(loaded.rows.map((row) => row.mandate_id));
  const kept = pickBy(takes.value, (volume, mandateId) =>
    offered.has(Number(mandateId))
  );
  if (Object.keys(kept).length !== Object.keys(takes.value).length) {
    takes.value = kept;
  }
};

const currentConfirmRequest = (): PickerConfirmRequest | null => {
  const answered = mandates.value;
  const acceptedQuery = acceptedPickerQuery.value;
  if (answered === null || acceptedQuery === null) return null;

  return confirmRequest(
    identity.value,
    acceptedQuery.criteria,
    answered.periods,
    takes.value
  );
};

const loadMandates = async () => {
  const requestedPeriods = [...periods.value];
  if (requestedPeriods.length === 0) return;

  const requestGeneration = ++mandateRequestGeneration;
  const requestKey = mandateQueryKey.value;
  const query = pickerQuery(
    criteria.value,
    minPriceFilter.value,
    requestedPeriods,
    takenMandateIds.value
  );

  loading.value = true;
  try {
    const response = await forwardsApi.getPickerMandates(query);
    if (
      requestGeneration !== mandateRequestGeneration ||
      requestKey !== mandateQueryKey.value
    )
      return;

    mandates.value = response.data;
    acceptedPickerQuery.value = query;
    acceptedMandatesGeneration += 1;
    loadError.value = null;
    pruneTakes(response.data);
    if (query.taken_mandate_ids.join(",") === takenMandateKey.value) {
      applyAdvice(response.data);
    } else {
      void loadSelectionAdvice();
    }
  } catch (error) {
    if (
      requestGeneration !== mandateRequestGeneration ||
      requestKey !== mandateQueryKey.value
    )
      return;

    loadError.value = error;
    mandates.value = null;
    acceptedPickerQuery.value = null;
    acceptedMandatesGeneration += 1;
    adviceRequestGeneration += 1;
    // The advice describes an answer that is now gone; leaving it up would price a table nothing
    // is rendering.
    hint.value = null;
    productWarnings.value = [];
  } finally {
    if (
      requestGeneration === mandateRequestGeneration &&
      requestKey === mandateQueryKey.value
    )
      loading.value = false;
  }
};

const applyAdvice = (loaded: PickerMandates) => {
  hint.value = loaded.hint;
  productWarnings.value = loaded.product_warnings;
};

/**
 * Re-asks the advice about the mandates now taken, and nothing else.
 *
 * `deal_picker_service.qualifying_mandates` reads `taken_mandate_ids` only to choose the mandates the hint
 * and the warnings describe (`_selected_mandates`) — the rows themselves depend on the criteria and the
 * range — so the answer's rows are dropped on purpose here. Leaving `mandates` alone is what keeps a
 * keystroke from rebuilding the table, and the caret in it (FIX4-4 point 4).
 */
const loadSelectionAdvice = async () => {
  const answered = mandates.value;
  const acceptedQuery = acceptedPickerQuery.value;
  if (answered === null || acceptedQuery === null) return;

  const requestGeneration = ++adviceRequestGeneration;
  const mandatesGeneration = acceptedMandatesGeneration;
  const selectionKey = takenMandateKey.value;
  const query: PickerQuery = {
    ...acceptedQuery,
    periods: answered.periods,
    taken_mandate_ids: takenMandateIds.value,
  };

  try {
    const response = await forwardsApi.getPickerMandates(query);
    if (
      requestGeneration !== adviceRequestGeneration ||
      mandatesGeneration !== acceptedMandatesGeneration ||
      selectionKey !== takenMandateKey.value
    )
      return;
    applyAdvice(response.data);
  } catch (error) {
    if (
      requestGeneration !== adviceRequestGeneration ||
      mandatesGeneration !== acceptedMandatesGeneration ||
      selectionKey !== takenMandateKey.value
    )
      return;
    // Advisory: the table on screen stays usable and the last advice stands, but a failure is still a
    // real fault.
    globalErrorHandler(error);
  }
};

const refresh = () => {
  if (rowsOutdated.value || mandates.value === null) {
    rowsOutdated.value = false;
    void loadMandates();
  } else {
    void loadSelectionAdvice();
  }
};

const debouncedRefresh = debounce(refresh, REFETCH_DEBOUNCE_MS);

const startConfirm = () => {
  createError.value = null;
  if (!canConfirm.value) {
    createError.value = confirmBlockedReason.value;
    return;
  }
  const request = currentConfirmRequest();
  if (request !== null) void createDeal(request);
};

const createDeal = async (request: PickerConfirmRequest) => {
  if (!canCreateDeals.value) return;
  creating.value = true;
  createError.value = null;
  try {
    const response = await forwardsApi.createDeal(request);
    await router.push(dealRoute(response.data.id));
  } catch (error) {
    createError.value =
      forwardsErrorMessage(error) ?? t("forwards.picker.errors.create_failed");
  } finally {
    creating.value = false;
  }
};

watch(mandateQueryKey, () => {
  mandateRequestGeneration += 1;
  loading.value = false;
  rowsOutdated.value = true;
  debouncedRefresh();
});

watch(takenMandateKey, () => {
  hint.value = null;
  productWarnings.value = [];
  adviceRequestGeneration += 1;
  debouncedRefresh();
});

onMounted(refresh);

// A request still in flight after navigation must not apply its answer, start follow-up work, or
// report its failure on the page the user moved to.
onUnmounted(() => {
  debouncedRefresh.cancel();
  mandateRequestGeneration += 1;
  adviceRequestGeneration += 1;
});
</script>

<style lang="scss" scoped>
.volume-picker-page {
  flex-grow: 1;
  text-align: left;
  overflow-y: auto;
  max-width: 100%;
}

// A flex item never shrinks below its content's width, and the take table's period columns are
// wider than the space beside the summary, so without this the summary wraps below the table.
.picker-take-column {
  min-width: 0;
}
</style>
